/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {RadioGroup} from '@headlessui/react'
import clsx from 'clsx'
import {ExclamationTriangleIcon} from '@heroicons/react/24/solid'
import {PackagesItemsPerson} from 'types/entities/PackagesItems'
import {usePackageItemKartingOptions} from 'utils/packageUtils'
import {PackageItemPersonType} from 'types/enums/PackageItemPersonType'
import {useHallOpeningHours} from 'utils/hooks/useHallOpeningHours'
import {useTypedSelector} from 'helpers/reducers'
import dayjs from 'dayjs'

interface BookingPackageItemsDurationsProps {
    chosenDuration: number | undefined
    setChosenDuration: (val: number) => void
    enabled: boolean
    withKarting?: number
    className?: string
    personType: PackageItemPersonType
    disabled2Hours: boolean
    disabled1Hour: boolean
    isSeller?: boolean
}

const BookingPackageItemsDurations = (
    props: BookingPackageItemsDurationsProps
) => {
    const { t } = useTranslation()
    const [chosenDuration, setChosenDuration] = useState<number | undefined>(
        undefined
    )
    const { getPackageItemForCombination } = usePackageItemKartingOptions()
    const hallOpeningHours = useHallOpeningHours()
    const selectedTimeslot = useTypedSelector(
        (state) => state.bookingState.bookingProcess.selectedTimeslot
    )
    const timeslotDate = selectedTimeslot?.start_at
        ? dayjs(selectedTimeslot.start_at)
        : null

    const durationOptions = props.disabled2Hours
        ? [
              { name: 3 + ' ' + t('hours'), enabled: true, value: 180 },
              { name: 4 + ' ' + t('hours'), enabled: true, value: 240 },
              { name: 4.5 + ' ' + t('hours'), enabled: true, value: 270 },
              { name: 6 + ' ' + t('hours'), enabled: true, value: 360 },
              { name: 8 + ' ' + t('hours'), enabled: true, value: 480 },
          ]
        : props.disabled1Hour
          ? [
                { name: 2 + ' ' + t('hours'), enabled: true, value: 120 },
                { name: 3 + ' ' + t('hours'), enabled: true, value: 180 },
                { name: 4 + ' ' + t('hours'), enabled: true, value: 240 },
                { name: 4.5 + ' ' + t('hours'), enabled: true, value: 270 },
                { name: 6 + ' ' + t('hours'), enabled: true, value: 360 },
                { name: 8 + ' ' + t('hours'), enabled: true, value: 480 },
            ]
          : [
                { name: 1 + ' ' + t('hour'), enabled: true, value: 60 },
                { name: 2 + ' ' + t('hours'), enabled: true, value: 120 },
                { name: 3 + ' ' + t('hours'), enabled: true, value: 180 },
                { name: 4 + ' ' + t('hours'), enabled: true, value: 240 },
                { name: 4.5 + ' ' + t('hours'), enabled: true, value: 270 },
                { name: 6 + ' ' + t('hours'), enabled: true, value: 360 },
                { name: 8 + ' ' + t('hours'), enabled: true, value: 480 },
            ]

    const isPackageItemDisabledForCombination = (duration: number) => {
        if (!props.enabled) {
            return true
        }
        const packagesItemsPerson: PackagesItemsPerson | undefined =
            getPackageItemForCombination(
                duration,
                !!props.withKarting,
                props.personType
            )
        return packagesItemsPerson === undefined
    }

    const setChosenDurationHandler = (option: number) => {
        setChosenDuration(option)
        props.setChosenDuration(option)
    }

    // useEffect(() => {
    //     const isDisabledFromPackageItem = isPackageItemDisabledForCombination(props.chosenDuration);
    //     setDisabled(isDisabledFromPackageItem);
    // }, [props.chosenDuration]);

    useEffect(() => {
        setChosenDuration(
            props?.chosenDuration ? props.chosenDuration : undefined
        )
    }, [props.chosenDuration])

    const colorClasses = {
        'red-bg': 'bg-arbo-red',
        'yellow-bg': 'bg-arbo-yellow',
        'red-all': 'bg-arbo-red border-arbo-red',
        'yellow-all': 'bg-arbo-yellow border-arbo-yellow',
        'black-all': 'bg-gray-900 border-gray-900',
        'black-bg': 'bg-gray-900',
        'top-black':
            'hover:bg-gray-900 data-checked:bg-white data-checked:ring-0 data-checked:border-gray-900 data-checked:ring-gray-900 data-checked:text-gray-900',
        'top-red':
            'hover:bg-arbo-red data-checked:bg-white data-checked:ring-0 data-checked:border-arbo-red data-checked:ring-arbo-red data-checked:text-arbo-red',
        'top-yellow':
            'hover:bg-arbo-yellow data-checked:bg-white data-checked:ring-0 data-checked:border-arbo-yellow data-checked:ring-arbo-yellow data-checked:text-arbo-yellow',
    }

    const colorClass = props.withKarting == 1 ? 'red' : 'yellow'

    return (
        <div
            className={clsx(
                'BookingPackageItemsDurations flex w-full',
                props.className
            )}
        >
            <fieldset
                aria-label={t('Choose a duration of your stay')}
                className={clsx('w-full sm:w-auto')}
            >
                <RadioGroup
                    className={clsx(
                        'flex flex-row w-full gap-3'
                    )}
                    value={chosenDuration ?? 0}
                    onChange={setChosenDurationHandler}
                >
                    {durationOptions.map((option) => {
                        const isDisabledFromPackageItem =
                            isPackageItemDisabledForCombination(option.value)
                        const showWarning: boolean =
                            props.isSeller == undefined
                                ? false
                                : hallOpeningHours.packageHasEndedAfterHallOpeningHours(
                                    timeslotDate,
                                    option.value / 60
                                )

                        if (isDisabledFromPackageItem) {
                            return (
                                <React.Fragment
                                    key={option.name}
                                ></React.Fragment>
                            )
                            // return <UnavailableTime key={option.name} />;
                        }
                        return (
                            <RadioGroup.Option
                                key={option.name}
                                className={({ checked }) =>
                                    clsx(
                                        props.enabled && option.enabled
                                            ? 'cursor-pointer'
                                            : 'cursor-not-allowed opacity-40',
                                        'flex-1 relative flex items-center justify-center rounded-lg px-3 py-3 text-sm font-semibold border-2 transition-all duration-200 shadow-sm hover:shadow-md whitespace-nowrap',
                                        checked
                                            ? 'bg-blue-500 border-blue-500 text-white shadow-md'
                                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:border-blue-400 dark:hover:border-blue-500'
                                    )
                                }
                                disabled={
                                    isDisabledFromPackageItem ||
                                    !props.enabled ||
                                    !option.enabled
                                }
                                value={option.value}
                            >
                                {({ checked }) => (
                                    <div className={'flex items-center gap-2 justify-center'}>
                                        <span>{option.name}</span>
                                        {showWarning && (
                                            <ExclamationTriangleIcon className={'w-4 h-4 text-red-500'} />
                                        )}
                                    </div>
                                )}
                            </RadioGroup.Option>
                        )
                    })}
                </RadioGroup>
            </fieldset>
        </div>
    )
}

export default BookingPackageItemsDurations
